import { useEffect, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useCompanyDoc, useCompanyDocs } from '../../hooks/useDocs';
import { ApiClientError } from '../../services/api/client';
import { Spinner } from '../../components/common/Spinner';
import { DocMarkdown } from '../../components/docs/DocMarkdown';

/**
 * MKS-508 — the company docs reader: a doc list on the left, one rendered doc on
 * the right. Mounted at /docs and /docs/:slug.
 */
export function DocsPage() {
  const { slug } = useParams<{ slug?: string }>();
  const list = useCompanyDocs();
  const doc = useCompanyDoc(slug);
  const { hash } = useLocation();
  const slugs = useMemo(() => new Set((list.data ?? []).map((d) => d.slug)), [list.data]);
  const rendered = Boolean(doc.data && list.data);

  // Scroll to the heading a link points at; with no hash, start a new doc at the top.
  useEffect(() => {
    if (!rendered) return;
    if (hash) {
      let id = hash.slice(1);
      try {
        id = decodeURIComponent(id);
      } catch {
        // keep the raw fragment
      }
      document.getElementById(id)?.scrollIntoView?.();
    } else {
      window.scrollTo?.(0, 0);
    }
  }, [slug, hash, rendered]);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 md:flex-row">
      <nav aria-label="Company docs" className="md:w-64 md:shrink-0">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">Docs</h2>
        {list.isLoading && <Spinner size="sm" />}
        {list.data && (
          <ul className="space-y-1">
            {list.data.map((d) => (
              <li key={d.slug}>
                <Link
                  to={`/docs/${d.slug}`}
                  aria-current={d.slug === slug ? 'page' : undefined}
                  className={`block rounded px-3 py-2 text-sm ${
                    d.slug === slug
                      ? 'bg-primary-50 font-medium text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {d.title}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </nav>

      <main className="min-w-0 flex-1">
        {!slug && (
          <>
            <h1 className="text-3xl font-bold text-gray-900">Company docs</h1>
            <p className="mt-3 text-gray-700">Pick a document from the list to read it here.</p>
          </>
        )}
        {slug && doc.isLoading && <Spinner />}
        {slug && doc.isError && (
          <div>
            {doc.error instanceof ApiClientError && doc.error.statusCode === 404 ? (
              <>
                <h1 className="text-xl font-semibold text-gray-900">Document not found</h1>
                <Link to="/docs" className="mt-2 inline-block text-primary-600 underline">
                  Back to company docs
                </Link>
              </>
            ) : (
              <p className="text-red-700">The document could not be loaded. Please try again.</p>
            )}
          </div>
        )}
        {slug && doc.data && list.data && <DocMarkdown markdown={doc.data.markdown} slugs={slugs} />}
      </main>
    </div>
  );
}
